import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';

export default function Landing() {
  const navigate = useNavigate();
  
  // Detect if we're on production (custom domain) or preview/emergent domain
  const isProduction = !window.location.hostname.includes('emergent') && 
                       !window.location.hostname.includes('localhost') &&
                       !window.location.hostname.includes('127.0.0.1');
  
  // Native Google OAuth handler (for production)
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await axios.post(`${API}/auth/google`, {
        credential: credentialResponse.credential
      }, {
        withCredentials: true
      });
      
      localStorage.setItem('user', JSON.stringify(response.data));
      // Land everyone on the Home portal — role-specific dashboards are just one
      // of several apps reachable from there.
      navigate('/home');
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Login failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed. Please try again.');
  };

  // Emergent auth handler (for preview)
  const handleEmergentLogin = () => {
    const redirectUri = encodeURIComponent(window.location.origin);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUri}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
        {/* Logo */}
        <div className="mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_disciple-hub-3/artifacts/wqhxldc5_Live%20Church%20Logo%20Print.png"
            alt="Live Church Logo"
            className="h-24 md:h-32 w-auto"
          />
        </div>
        
        {/* Main Title */}
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight text-center mb-4 animate-fade-in-up">
          Live Church
        </h1>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-amber-600 tracking-tight text-center mb-12 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          Discipleship Intensive
        </h2>
        
        {/* Login Button - Native Google for production, Emergent for preview */}
        <div className="animate-fade-in-up" style={{animationDelay: '0.2s'}}>
          {isProduction ? (
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              theme="filled_black"
              size="large"
              shape="pill"
              text="signin_with"
              logo_alignment="left"
            />
          ) : (
            <Button 
              onClick={handleEmergentLogin}
              className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-lg rounded-full flex items-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>
          )}
        </div>
        
        {/* Environment indicator for debugging (hidden in production) */}
        {!isProduction && (
          <p className="mt-4 text-xs text-slate-400">
            Preview Mode - Using Emergent Auth
          </p>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-slate-500 text-sm">
            Discipleship Intensive — Growing disciples, one lesson at a time.
          </p>
        </div>
      </footer>
    </div>
  );
}
